import { push } from 'react-router-redux';
import { purgeStoredState } from 'redux-persist';
import get from 'lodash/get';
import { AuthenticationDetails } from 'amazon-cognito-identity-js';

import persistConfig from '../../persistConfig';
import cognitoService from '../../cognito/cognito-service';
import communityLogout from '../Community/logout';
import * as constants from './constants';
import { reload, getUri } from '../../utils';

const jwtDecode = require('jwt-decode');

const newPasswordRequired = (payload) => (
  {
    type: constants.LOGIN_USER_NEW_PASSWORD_REQUIRED,
    payload,
  }
);

const logoutSuccess = () => (
  {
    type: constants.LOGOUT_USER_SUCCESS,
  }
);

let refreshTimeout;
let impersonate = '';

/**
 *
 * @param {*} viewer
 * Returns decoded token
 */
const getIdToken = (viewer) => (
  viewer ? viewer.idToken : null
);

/**
 *
 * @param {*} viewer
 * Returns original JWT token
 */
const getJwtToken = (viewer) => (
  viewer ? viewer.jwtToken : null
);

function setupSentryUserContext(viewer) {
  if (window.Sentry) {
    const idToken = viewer.idToken || {};
    window.Sentry.setUser({
      id: idToken.sub,
      email: idToken.email,
      username: viewer.userName,
    });
    window.Sentry.setExtra('scid', idToken['custom:institution']);
  }
}

const loginSuccess = (session, loginTime) => {
  const jwtToken = session.getIdToken().getJwtToken();
  const idTokenDecoded = jwtDecode(jwtToken);
  const payload = {
    jwtToken,
  };
  payload.idToken = idTokenDecoded;
  payload.isAuthenticated = true;
  payload.userName = idTokenDecoded['cognito:username'];
  payload.profile = {};
  payload.loginTime = loginTime;
  payload.errorCode = '';
  payload.impersonator = idTokenDecoded.impersonator;
  payload.impersonatorToken = idTokenDecoded.impersonatorToken && jwtDecode(idTokenDecoded.impersonatorToken);
  // Allow for test impersonation in local dev env, but don't override actual impersonation attempt
  /* istanbul ignore if */
  if (process.env.__DEVCLIENT__ && !payload.impersonator && impersonate !== '') {
    payload.impersonator = true;
    payload.impersonatorToken = { roles: [impersonate] };
  }
  payload.institutionName = idTokenDecoded.institutionName;
  return {
    type: constants.LOGIN_USER_SUCCESS,
    payload,
  };
};

const loginFailure = (err) => {
  let errorCode = err.code;
  if (errorCode === 'UserLambdaValidationException') {
    errorCode = err.message.includes('locked') ? 'UserIsLocked' : 'InvalidPasswordException';
  }
  if (errorCode === 'NotAuthorizedException') {
    errorCode = 'LoginFailedNotAuthorizedException';
  }
  const payload = {
    errorCode,
  };
  return {
    type: constants.LOGIN_USER_FAILURE,
    payload,
  };
};

const completeNewPassword = (user) => (
  (dispatch) => {
    const cognitoUser = cognitoService.getCognitoUser(user.username);
    cognitoUser.Session = user.Session;
    cognitoUser.completeNewPasswordChallenge(user.password, {}, {
      onSuccess: (result) => handleLoginSuccess(dispatch, result, Date.now()), // eslint-disable-line no-use-before-define
      onFailure: (err) => dispatch(loginFailure(err)),
    });
  }
);

const logout = (viewer) => (
  async (dispatch) => {
    cognitoService.logout();
    if (get(getIdToken(viewer), 'sub')) {
      await communityLogout(get(getIdToken(viewer), 'sub'));
    }
    clearTimeout(refreshTimeout);
    dispatch(logoutSuccess());
    return purgeStoredState(persistConfig).then(() => {
      reload();
    });
  }
);

const refreshSession = (viewer) => (dispatch) => {
  // can't refresh impersonator token, log the user out
  if (viewer.impersonator) {
    return dispatch(logout(viewer)).then(() => Promise.reject(new Error('Not Supported')));
  }
  return cognitoService.getCurrentUserWithSession()
    .then((currentUser) => {
      const session = currentUser.getSignInUserSession();
      const { loginTime } = viewer; // maintain original login time
      if (viewer.jwtToken !== session.getIdToken().getJwtToken()) {
        return handleLoginSuccess(dispatch, session, loginTime); // eslint-disable-line no-use-before-define
      } else {
        return new Promise((resolve, reject) => {
          currentUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
            if (err) {
              reject(new Error('Could not create user session'));
            } else {
              resolve(handleLoginSuccess(dispatch, newSession, loginTime)); // eslint-disable-line no-use-before-define
            }
          });
        });
      }
    })
    .catch((err) => {
      console.log(err); // eslint-disable-line no-console
      return dispatch(logout(viewer));
    });
};

function setupTokenRefreshTimer(dispatch, viewer) {
  const expirationMs = get(getIdToken(viewer), 'exp', 0) * 1000;
  const timeout = expirationMs - Date.now() - (2000 * 60); // refresh 2 minutes before expiration
  // const timeout = 2000; // refresh 2 minutes before expiration
  if (timeout < 0) {
    return dispatch(refreshSession(viewer));
  } else {
    refreshTimeout = setTimeout(() => {
      dispatch(refreshSession(viewer));
    }, Math.min(timeout, 0x7FFFFFFF));
    return Promise.resolve();
  }
}

const appRefreshed = (viewer) => async (dispatch) => {
  const uri = getUri();
  const hashIndex = uri.indexOf('#');
  const angularIndex = uri.indexOf('#!');
  if (hashIndex !== -1 && angularIndex === -1) {
    const dummySession = {
      getIdToken: () => ({
        getJwtToken: () => uri.substring(hashIndex + 1),
      }),
    };
    window.history.replaceState({}, document.title, window.location.pathname);
    return dispatch(loginSuccess(dummySession, Date.now()));
  }
  if (viewer.isAuthenticated) {
    setupSentryUserContext(viewer);
    await setupTokenRefreshTimer(dispatch, viewer);
  }
  return null;
};

function handleLoginSuccess(dispatch, session, loginTime) {
  const loginSuccessRedux = dispatch(loginSuccess(session, loginTime));
  setupSentryUserContext(loginSuccessRedux.payload);
  setupTokenRefreshTimer(dispatch, loginSuccessRedux.payload);
  return loginSuccessRedux;
}

const login = (user) => (
  (dispatch) => {
    impersonate = user.impersonate || '';
    const cognitoUser = cognitoService.getCognitoUser(user.username);
    const authenticationDetails = new AuthenticationDetails({
      Username: user.username,
      Password: user.password,
    });
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (payload) => handleLoginSuccess(dispatch, payload, Date.now()),
      onFailure: (err) => dispatch(loginFailure(err)),
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        dispatch(newPasswordRequired({
          cognitoUser: {
            username: cognitoUser.username,
            Session: cognitoUser.Session,
            email: user.username,
          },
        }));
        dispatch(push('/new-password'));
      },
    });
  }
);

const clearLoginErrors = () => ({ type: constants.LOGIN_CLEAR_ERRORS, payload: { errorCode: null } });

export { login, logout, completeNewPassword, appRefreshed, refreshSession, getIdToken, getJwtToken, clearLoginErrors };
